import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { FieldTitle, useInput, useTranslate, type InputProps } from "ra-core";
import {
  FormControl,
  FormError,
  FormField,
  FormLabel,
} from "@/components/admin/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export type PasswordInputProps = InputProps &
  Omit<React.ComponentProps<"input">, "type"> & {
    inputClassName?: string;
  };

/**
 * Passordfelt med vis/skjul-knapp.
 *
 * Bygget på de samme form-primitivene som `TextInput` i stedet for å pakke
 * den inn: knappen må ligge på selve inputen, og `TextInput` tegner etikett,
 * felt og feilmelding som én blokk. Pakket inn ville knappen flyttet på seg
 * i det en valideringsfeil dukket opp under feltet.
 */
export const PasswordInput = ({
  label,
  className,
  inputClassName,
  validate: _validate,
  format: _format,
  parse: _parse,
  ...props
}: PasswordInputProps) => {
  const [synlig, setSynlig] = useState(false);
  const translate = useTranslate();
  const { id, field, isRequired } = useInput({
    ...props,
    label,
    validate: _validate,
  } as InputProps);

  return (
    <FormField id={id} className={className} name={field.name}>
      {label !== false && (
        <FormLabel>
          <FieldTitle
            label={label}
            source={props.source}
            isRequired={isRequired}
          />
        </FormLabel>
      )}
      <div className="relative">
        <FormControl>
          <Input
            {...props}
            {...field}
            type={synlig ? "text" : "password"}
            // Plass til knappen, så et langt passord ikke løper under den.
            className={cn("pr-11", inputClassName)}
          />
        </FormControl>
        <button
          type="button"
          onClick={() => setSynlig((v) => !v)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          aria-label={
            synlig
              ? translate("ra.input.password.toggle_visible", {
                  _: "Skjul passord",
                })
              : translate("ra.input.password.toggle_hidden", {
                  _: "Vis passord",
                })
          }
          aria-pressed={synlig}
        >
          {synlig ? (
            <EyeOff className="size-4" aria-hidden="true" />
          ) : (
            <Eye className="size-4" aria-hidden="true" />
          )}
        </button>
      </div>
      <FormError />
    </FormField>
  );
};
